import { RSFundPageModel } from '../../../../../types/server-data/ClientConfig';
import { AxiosInstance } from '../../../../../utilities/requestClient';

/** `[label, scenarioKey]` rows for the RST Fixed scenario dropdown. */
export type RstFixedLrstKeyRow = [string, string];

/** Backend may return rows, or `{ error: string }` when RA3 has no stored report. */
export type RstFixedLrstKeysPayload = RstFixedLrstKeyRow[] | { error: string };

/** One stress-test scenario (lists used by `fixedRst.data` builders). */
export type RstFixedStressTestScenario = Record<string, unknown> & {
  selected_position_date?: string;
  name?: string;
  risk_list?: unknown[][];
  dynamic_list?: unknown[][];
  ucits_list?: unknown[][];
  riaif_list?: unknown[][];
  act40_list?: unknown[][];
  sif_list?: unknown[][];
  fil_list?: unknown[][];
};

/** `rst_fixed_page` body: compliance + stress for the requested LRST only. */
export type RstFixedPagePayload = {
  compliance_checklist: Record<string, unknown>;
  stress_test_data: RstFixedStressTestScenario;
};

/**
 * Normalises `lrst_keys` from the RST Fixed page payload (array of pairs vs RA3 error object).
 */
export function getRstFixedLrstKeyRows(
  lrst_keys: RstFixedLrstKeysPayload | undefined,
): {
  rows: RstFixedLrstKeyRow[];
  backendError: string | null;
} {
  if (lrst_keys == null) {
    return { rows: [], backendError: null };
  }
  if (Array.isArray(lrst_keys)) {
    const rows = lrst_keys.filter(
      (row): row is RstFixedLrstKeyRow =>
        Array.isArray(row) &&
        row.length >= 2 &&
        typeof row[0] === 'string' &&
        typeof row[1] === 'string',
    );
    return { rows, backendError: null };
  }
  if (
    typeof lrst_keys === 'object' &&
    lrst_keys !== null &&
    'error' in lrst_keys
  ) {
    const raw = (lrst_keys as { error: unknown }).error;
    const backendError =
      typeof raw === 'string' && raw.trim()
        ? raw
        : 'No scenario data available.';
    return { rows: [], backendError };
  }
  return { rows: [], backendError: null };
}

export function rstFixedStressTestsAreEmpty(
  stress_test_data: RstFixedPagePayload['stress_test_data'] | undefined,
): boolean {
  if (stress_test_data == null || typeof stress_test_data !== 'object') return true;
  return Object.keys(stress_test_data).length === 0;
}

export type RstFixedPageResponse = RSFundPageModel<RstFixedPagePayload>;

// ---- Compliance type (used by RST fixed section config only) ----
export type ComplianceType =
  | 'UCITS'
  | 'RIAIF'
  | 'ACT40'
  | 'NURS'
  | 'QIAIF'
  | 'PARTII'
  | 'FIVG';

/** LRST / FMV key list only (pairs or `{ error }`). */
export const getRstFixedDropdownOptions = async (fund_name: string) => {
  const { data } = await AxiosInstance.get<RstFixedLrstKeysPayload>(
    `get_lrst_scenario_keys/${fund_name}`,
  );

  return data;
};

/**
 * Compliance checklist + stress payload for one LRST scenario.
 * Pass `lrstKey` so the backend returns data for that dropdown selection.
 */
export const getRstFixedPage = async (
  fundId: string,
  lrstKey: string,
  positionDate?: string | null,
) => {
  let url = !positionDate
    ? `rst_fixed_page/${fundId}/${lrstKey}`
    : `rst_fixed_page/${fundId}/${lrstKey}/${positionDate}`;
  const { data } = await AxiosInstance.get<RstFixedPageResponse>(url);
  return data;
};
